import { useState } from "react";
import { X, Mail, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function signInWithGoogle() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export default function LoginDialog({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    if (status === "sending") return;
    setError("");
    setStatus("sending");
    try {
      await axios.post(`${API}/auth/magic-link`, { email: email.trim().toLowerCase() }, { withCredentials: true });
      setStatus("sent");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Non è stato possibile inviare il link. Riprova.");
      setStatus("idle");
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="login-dialog">
      <div className="absolute inset-0 bg-[#3B443D]/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl border border-[#E4E7E1] shadow-xl p-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#6A7368] hover:text-[#3B443D]"
          data-testid="login-dialog-close"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        {status === "sent" ? (
          <div className="text-center" data-testid="login-sent">
            <CheckCircle2 className="mx-auto text-[#A3B897]" size={44} strokeWidth={1.5} />
            <h2 className="mt-4 font-serif-display font-semibold text-2xl text-[#3B443D]">Controlla la tua email</h2>
            <p className="mt-3 text-sm text-[#58635B]">
              Abbiamo inviato il link di accesso a <b className="break-all">{email.trim().toLowerCase()}</b>.
              Vale 15 minuti e si apre con un tocco, anche dal telefono.
            </p>
            <p className="mt-3 text-[12px] text-[#6A7368]">
              Non la trovi? Controlla la cartella spam o promozioni.
            </p>
            <button
              onClick={() => setStatus("idle")}
              className="mt-5 text-sm text-[#58635B] underline underline-offset-4 decoration-[#A3B897]/60"
              data-testid="login-send-again"
            >
              Usa un'altra email
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="tl-label">Tempo leggero</div>
            <h2 className="mt-2 font-serif-display font-semibold text-3xl text-[#3B443D]">Accedi</h2>
            <p className="mt-3 text-sm text-[#58635B]">
              Niente password: entra con Google oppure ricevi un link via email.
              Se hai già il planner, usa la stessa email con cui l'hai acquistato.
            </p>
            <button
              type="button"
              onClick={signInWithGoogle}
              className="mt-5 w-full inline-flex items-center justify-center gap-3 px-6 py-3 bg-white border border-[#E4E7E1] hover:border-[#A3B897] hover:bg-[#FDFDFB] rounded-full text-sm font-medium text-[#3B443D] transition-colors"
              data-testid="login-google"
            >
              <GoogleIcon /> Continua con Google
            </button>
            <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-[#9AA397]">
              <span className="flex-1 h-px bg-[#E4E7E1]" /> oppure <span className="flex-1 h-px bg-[#E4E7E1]" />
            </div>
            <div className="mt-5 relative">
              <Mail size={16} strokeWidth={1.75} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8FA087]" />
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="la-tua@email.it"
                className="w-full pl-11 pr-4 py-3 bg-[#FDFDFB] border border-[#E4E7E1] rounded-full text-sm text-[#3B443D] placeholder:text-[#9AA397] focus:outline-none focus:border-[#A3B897]"
                data-testid="login-email-input"
              />
            </div>
            {error && (
              <div className="mt-3 text-sm text-[#C48B97]" data-testid="login-error">{error}</div>
            )}
            <button
              type="submit"
              disabled={status === "sending"}
              className="tl-btn-primary mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#A3B897] hover:bg-[#8FA087] text-white rounded-full text-sm font-medium transition-all disabled:opacity-60"
              data-testid="login-submit"
            >
              {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} strokeWidth={1.75} />}
              {status === "sending" ? "Invio in corso…" : "Inviami il link di accesso"}
            </button>
            <div className="mt-3 text-[11px] text-[#6A7368] text-center">
              Resterai dentro per 15 giorni, anche dal telefono.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
